import React, { useState } from 'react';
import { Clock, AlertTriangle, CheckCircle, MessageSquare } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { REVIEW_REASON_CODES } from '@/lib/types/completion';
import type { DeadlineType } from '@/lib/types/job';

interface ClientReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReview: (result: 'accepted' | 'rejected', notes?: string, reasonCode?: string) => Promise<boolean>;
  loading: boolean;
  submittedAt: string;
  deliveryDate?: string;
  dueDate: string;
  deadlineType?: DeadlineType;
  timeRemaining: { days: number; hours: number; deadline: Date } | null;
  onJobAccepted?: () => void;
  purchaseOrderId?: string;
  completionNotes?: string;
}

export const ClientReviewModal: React.FC<ClientReviewModalProps> = ({
  open,
  onOpenChange,
  onReview,
  loading,
  submittedAt,
  deliveryDate,
  dueDate,
  deadlineType,
  timeRemaining,
  onJobAccepted,
  purchaseOrderId,
  completionNotes
}) => {
  const [reviewResult, setReviewResult] = useState<'accepted' | 'rejected' | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reasonCode, setReasonCode] = useState<string>('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    if (isSubmitting) return; // Prevent double-click
    setIsSubmitting(true);
    
    try {
      const success = await onReview('accepted', reviewNotes.trim() || undefined);
      if (success) {
        resetForm();
        onOpenChange(false);
        // Trigger callback to parent - it will handle refetch and review flow
        onJobAccepted?.();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!reasonCode || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      const success = await onReview('rejected', reviewNotes.trim() || undefined, reasonCode);
      if (success) {
        resetForm();
        onOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setReviewResult(null);
    setReviewNotes('');
    setReasonCode('');
    setShowRejectForm(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Review Job Completion
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            The vendor has marked this job as complete on {deliveryDate ? formatDate(deliveryDate) : formatDate(submittedAt)}.
          </div>

          <div className="p-4 rounded-lg border bg-card space-y-3">
            <h4 className="font-medium">Completion Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-green-600">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                All milestones completed
              </div>
              <div className="flex items-center gap-2 text-green-600">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                Proof of Delivery uploaded
              </div>
              <div className="flex items-center gap-2 text-green-600">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                Vendor completion date: {deliveryDate ? new Date(deliveryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not specified'}
              </div>
          </div>
          </div>

          {completionNotes && (
            <div className="p-4 rounded-lg border bg-muted/50 space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Vendor's Completion Notes
              </h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {completionNotes}
              </p>
            </div>
          )}

          {timeRemaining && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <Clock className="h-4 w-4 text-warning" />
              <div className="text-sm">
                <span className="font-medium">Time remaining to review:</span>{' '}
                {timeRemaining.days > 0 && `${timeRemaining.days} day${timeRemaining.days !== 1 ? 's' : ''}, `}
                {timeRemaining.hours} hour{timeRemaining.hours !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          {!showRejectForm && (
            <Alert className="border-warning/50 bg-warning/10">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning">Payment Release</AlertTitle>
              <AlertDescription className="text-sm">
                Upon accepting this completion, protected funds will be immediately released to the vendor. 
                This action is <strong>irreversible</strong> and cannot be undone. Please ensure you have thoroughly reviewed 
                all deliverables before proceeding.
              </AlertDescription>
            </Alert>
          )}

          {!showRejectForm ? (
            <div className="space-y-4">
              <div className="text-sm font-medium">
                Do you accept this job as complete?
              </div>

              <div className="space-y-2">
                <Label htmlFor="review-notes">
                  Review Notes (Optional)
                </Label>
                <Textarea
                  id="review-notes"
                  placeholder="Add any notes about your review..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm font-medium text-destructive">
                Please provide a reason for rejecting this completion:
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason-code">Rejection Reason *</Label>
                <Select value={reasonCode} onValueChange={setReasonCode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_REASON_CODES.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reject-notes">
                  Detailed Feedback *
                </Label>
                <Textarea
                  id="reject-notes"
                  placeholder="Explain what needs to be addressed before the job can be accepted..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  required
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {!showRejectForm ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setShowRejectForm(true)}
              >
                Reject with Feedback
              </Button>
              <Button 
                onClick={handleAccept}
                disabled={loading || isSubmitting}
              >
                {(loading || isSubmitting) ? "Processing..." : "Accept & Close Job"}
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => setShowRejectForm(false)}
              >
                Back
              </Button>
              <Button 
                variant="destructive"
                onClick={handleReject}
                disabled={loading || isSubmitting || !reasonCode || !reviewNotes.trim()}
              >
                {(loading || isSubmitting) ? "Rejecting..." : "Reject Completion"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};